/** @jsxImportSource @emotion/react */
import { useMemo, useEffect } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import { ProjectMetricViewProps } from "@phoenix/pages/project/metrics/types";
import { costFormatter } from "@phoenix/utils/numberFormatUtils";
import { ChartTypeRenderer, type ChartType } from "@phoenix/pages/project/metrics/ChartTypeRenderer";
import type { TopUsersByCostQuery } from "./__generated__/TopUsersByCostQuery.graphql";

const FALLBACK_LABEL = "(unknown)";
const MAX_ITEMS = 10;
const COLOR_PALETTE = ["#6366f1", "#8b5cf6", "#a78bfa", "#7c3aed", "#5b21b6", "#4c1d95", "#818cf8", "#c4b5fd", "#ddd6fe", "#ede9fe"];

interface Props extends ProjectMetricViewProps {
  chartType?: ChartType;
}

export function TopUsersByCost({ projectId, timeRange, chartType = "vertical_bar", widgetId, widgetTitle, onDataExport }: Props) {
  const data = useLazyLoadQuery<TopUsersByCostQuery>(
    graphql`
      query TopUsersByCostQuery($projectId: ID!, $timeRange: TimeRange!) {
        project: node(id: $projectId) {
          ... on Project {
            spans(timeRange: $timeRange, rootSpansOnly: true, first: 500) {
              edges {
                span: node {
                  username
                  trace { costSummary { total { cost } } }
                }
              }
            }
          }
        }
      }
    `,
    {
      projectId,
      timeRange: {
        start: timeRange.start?.toISOString(),
        end: timeRange.end?.toISOString(),
      },
    }
  );

  const chartData = useMemo(() => {
    const edges = data.project?.spans?.edges ?? [];
    const map: Record<string, number> = {};
    for (const { span } of edges) {
      const key = span.username ?? FALLBACK_LABEL;
      map[key] = (map[key] ?? 0) + (span.trace?.costSummary?.total?.cost ?? 0);
    }
    return Object.entries(map)
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, MAX_ITEMS);
  }, [data]);

  useEffect(() => {
    if (chartData.length > 0 && onDataExport && widgetId && widgetTitle) {
      onDataExport(widgetId, widgetTitle, chartData);
    }
  }, [chartData, onDataExport, widgetId, widgetTitle]);



  return (
    <ChartTypeRenderer
      data={chartData}
      chartType={chartType}
      valueFormatter={costFormatter}
      colorPalette={COLOR_PALETTE}
      emptyIcon="👤"
      emptyText="No user cost data"
    />
  );
}
